//#region src/utils.ts
function e(e) {
	return e.replace(/_([a-z])/g, (e, t) => t.toUpperCase());
}
function t(n) {
	if (Array.isArray(n)) return n.map((e) => t(e));
	if (typeof n == "object" && n && !(n instanceof Date)) {
		let r = {};
		for (let [i, a] of Object.entries(n)) r[e(i)] = t(a);
		return r;
	}
	return n;
}
//#endregion
//#region src/client.ts
var n = "[auth]", r = "/auth/callback";
function i(e) {
	return e.replace(/\/+$/, "").toLowerCase() === r;
}
var a = class extends Error {
	constructor(e, t) {
		super(t), this.reason = e, this.name = "AuthCallbackError";
	}
};
function o(e) {
	if (!e.startsWith("/") || e.startsWith("//") || e.includes("\\")) return "/";
	for (let t of e) if (t.charCodeAt(0) <= 32) return "/";
	try {
		let t = new URL(e, window.location.origin), n = decodeURIComponent(t.pathname);
		return t.origin !== window.location.origin || i(n) ? "/" : `${t.pathname}${t.search}${t.hash}`;
	} catch {
		return "/";
	}
}
var s = class {
	constructor(e) {
		this.authUrl = e.authUrl, this.loginUrl = e.loginUrl, this.appName = e.appName, console.debug(n, "AuthClient initialized", {
			authUrl: this.authUrl,
			appName: this.appName
		});
	}
	async checkSession() {
		let e;
		try {
			e = await fetch(`${this.authUrl}/session`, {
				method: "GET",
				credentials: "include",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json"
				}
			});
		} catch (e) {
			return console.warn(n, "Session check could not reach the sign-in service", e), { status: "unavailable" };
		}
		if (e.status >= 500) {
			let t = l(e);
			return console.warn(n, "Session check unavailable", {
				status: e.status,
				retryAfterSec: t
			}), t === void 0 ? { status: "unavailable" } : {
				status: "unavailable",
				retryAfterSec: t
			};
		}
		if (!e.ok) return console.debug(n, "No valid session", { status: e.status }), { status: "unauthenticated" };
		let r;
		try {
			r = t(await e.json());
		} catch (e) {
			return console.error(n, "Session check returned an unreadable body", e), { status: "unauthenticated" };
		}
		return !r.authenticated || !r.userId ? (console.debug(n, "Session check returned unauthenticated"), { status: "unauthenticated" }) : {
			status: "ok",
			user: c(r),
			sessionData: r
		};
	}
	async generatePKCE() {
		let e = /* @__PURE__ */ new Uint8Array(32);
		crypto.getRandomValues(e);
		let t = btoa(String.fromCharCode(...e)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, ""), n = new TextEncoder(), r = await crypto.subtle.digest("SHA-256", n.encode(t));
		return {
			verifier: t,
			challenge: btoa(String.fromCharCode(...new Uint8Array(r))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "")
		};
	}
	async initiateSSO(e) {
		let t = o(e ?? window.location.pathname + window.location.search), i = `${window.location.origin}${r}`, a = await this.generatePKCE();
		sessionStorage.setItem("pkce_verifier", a.verifier);
		let s = btoa(String.fromCharCode(...crypto.getRandomValues(/* @__PURE__ */ new Uint8Array(32)))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
		sessionStorage.setItem("oauth_state", s), sessionStorage.setItem("sso_return_path", t);
		let c = new URLSearchParams({
			response_type: "code",
			client_id: this.appName,
			redirect_uri: i,
			scope: "openid profile email",
			state: s,
			code_challenge: a.challenge,
			code_challenge_method: "S256"
		}), l = `${this.authUrl}/authorize?${c}`;
		console.debug(n, "Initiating OIDC authorization", { appName: this.appName }), window.location.href = l;
	}
	async handleCallback(e, r) {
		if (!e) throw new a("missing_code", "No authorization code provided in callback");
		let i = sessionStorage.getItem("oauth_state");
		if (!r || r !== i) throw new a(!r || !i ? "missing_state" : "state_mismatch", "Invalid state parameter");
		sessionStorage.removeItem("oauth_state");
		let o = sessionStorage.getItem("pkce_verifier");
		if (!o) throw new a("missing_verifier", "PKCE verifier not found");
		sessionStorage.removeItem("pkce_verifier");
		let s = await fetch(`${this.authUrl}/token`, {
			method: "POST",
			credentials: "include",
			headers: { "Content-Type": "application/x-www-form-urlencoded" },
			body: new URLSearchParams({
				grant_type: "authorization_code",
				client_id: this.appName,
				code: e,
				redirect_uri: `${window.location.origin}/auth/callback`,
				code_verifier: o
			})
		});
		if (!s.ok) {
			let e = await s.json().catch(() => ({ error: "Unknown error" })), t = typeof e == "object" && e && "error" in e ? String(e.error) : "Unknown error";
			throw Error(t || `Token exchange failed: ${s.statusText}`);
		}
		let l = await fetch(`${this.authUrl}/session`, {
			method: "GET",
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json"
			}
		});
		if (!l.ok) throw Error("Failed to fetch user session after token exchange");
		let u = t(await l.json());
		if (!u.authenticated || !u.userId) throw Error("No user data returned from session endpoint");
		let d = c(u);
		return console.debug(n, "OIDC authentication completed", { email: d.email }), {
			user: d,
			sessionData: u
		};
	}
	async touchSession(e) {
		let t = e ?? `${this.authUrl}/session/touch`;
		try {
			return (await fetch(t, {
				method: "GET",
				credentials: "include",
				headers: { Accept: "application/json" }
			})).ok;
		} catch (e) {
			return console.debug(n, "touchSession network error", e), !1;
		}
	}
	async logout() {
		try {
			await fetch(`${this.authUrl}/session/logout`, {
				method: "POST",
				credentials: "include",
				headers: { "Content-Type": "application/json" }
			}).catch((e) => {
				console.warn(n, "Logout call failed, continuing with cleanup", e);
			});
		} finally {
			sessionStorage.removeItem("pkce_verifier"), sessionStorage.removeItem("oauth_state"), sessionStorage.removeItem("sso_return_path"), window.location.href = this.loginUrlReturningHere();
		}
	}
	loginUrlReturningHere() {
		try {
			let e = new URL(this.loginUrl);
			return e.searchParams.set("return_to", `${window.location.origin}/`), e.toString();
		} catch {
			return this.loginUrl;
		}
	}
	getReturnPath() {
		let e = sessionStorage.getItem("sso_return_path") || "/";
		return sessionStorage.removeItem("sso_return_path"), o(e);
	}
};
function c(e) {
	return {
		id: e.userId,
		email: e.email,
		name: e.fullName || `${e.firstName || ""} ${e.lastName || ""}`.trim() || e.email,
		role: e.role?.toLowerCase() || "user",
		organizationId: e.organizationId,
		organizationName: e.organizationName,
		firstName: e.firstName,
		lastName: e.lastName,
		fullName: e.fullName,
		avatar: e.avatarUrl,
		avatarUrl: e.avatarUrl,
		authProvider: e.authProvider,
		platformAdmin: e.platformAdmin,
		inviterName: e.inviterName,
		guestHintDismissed: e.guestHintDismissed,
		homeApp: e.homeApp,
		homeUrl: e.homeUrl
	};
}
function l(e) {
	let t = e.headers?.get("Retry-After")?.trim();
	if (!t) return;
	if (/^\d+$/.test(t)) return Number(t);
	let n = Date.parse(t);
	if (!Number.isNaN(n)) return Math.max(0, Math.ceil((n - Date.now()) / 1e3));
}
//#endregion
export { o as a, i, a as n, t as o, s as r, r as t };

//# sourceMappingURL=client-C353vByp.js.map